import { useActionSheet } from '@expo/react-native-action-sheet'
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { Image } from 'expo-image'
import React, { useState } from 'react'
import { Linking, Platform, StyleSheet, View } from 'react-native'
import { Avatar, ListItem } from 'react-native-elements'
import { Dialog, Paragraph } from 'react-native-paper'
import { useDispatch, useSelector } from 'react-redux'
import { listStyle } from '~/styles/list'
import { AlertConfirmation } from '~components/AlertConfirmation'
import CacheableImage from '~components/CacheableImage'
import { FormButton } from '~components/FormButton'
import { ApplicationState } from '~redux'
import { logout } from '~redux/auth'
import ProfileService, { AvatarSource } from '~services/profile'
import { Theme } from '~theme'
import { useTheme } from '~theme/ThemeManager'

export default function SettingsScreen(): React.JSX.Element {
  const { mode, theme } = useTheme()
  const { user, profile } = useSelector((state: ApplicationState) => state.auth)
  const dispatch = useDispatch()
  const [showRemoveAvatarConfirmation, setShowRemoveAvatarConfirmation] = useState(false)

  const styles = getStyles(theme)

  const { showActionSheetWithOptions } = useActionSheet()

  const showImagePickerActionSheet = async () => {
    if (!user?.uid) {
      return
    }

    if (Platform.OS === 'web') {
      //TODO: Do not show 'remove image' option if one isn't set?
      const options = ['Choose Existing', 'Remove Image', 'Cancel']
      showActionSheetWithOptions(
        {
          options,
          cancelButtonIndex: options.length - 1,
        },
        async (buttonIndex) => {
          switch (buttonIndex) {
            case 0:
              await ProfileService.captureAvatar(user.uid, AvatarSource.Camera)
              break
            case 1:
              setShowRemoveAvatarConfirmation(true)
              break
          }
        },
      )
    } else {
      const exists = null === (await Image.getCachePathAsync(user.uid))

      const options = [
        'Take Photo',
        'Choose Existing',
        ...(exists ? ['Remove Image'] : []),
        'Cancel',
      ]
      showActionSheetWithOptions(
        {
          options,
          cancelButtonIndex: options.length - 1,
          destructiveButtonIndex: exists ? options.length - 2 : undefined,
        },
        async (buttonIndex) => {
          switch (buttonIndex) {
            case 0:
              await ProfileService.captureAvatar(user.uid, AvatarSource.Camera)
              break
            case 1:
              await ProfileService.captureAvatar(user.uid, AvatarSource.Gallery)
              break
            case 2:
              if (!exists) {
                break
              }

              setShowRemoveAvatarConfirmation(true)
              break
          }
        },
      )
    }
  }

  const openContactUs = () => {
    const url = 'https://chattalearning.com/contact/'
    if (Platform.OS === 'web') {
      window.open(url, '_blank')
    } else {
      Linking.openURL(url).catch((err) => console.error("Couldn't load page", err))
    }
  }

  return (
    <View style={styles.container}>
      <View style={styles.avatarContainer}>
        <Avatar
          rounded
          size={96}
          titleStyle={{ fontSize: 32 }}
          containerStyle={styles.avatar}
          placeholderStyle={styles.avatar}
          onPress={showImagePickerActionSheet}
          activeOpacity={0.6}
        >
          <View style={styles.placeholderContainer}>
            <CacheableImage
              style={{ width: '100%', height: '100%', borderRadius: 48 }}
              source={ProfileService.getProfileURL(user?.uid)}
              cachePolicy={'memory-disk'}
              placeholder={<FontAwesomeIcon color={theme[400]} size={38} icon={['fas', 'user']} />}
            />
          </View>
          <Avatar.Accessory size={28} />
        </Avatar>
        {showRemoveAvatarConfirmation && user?.uid && (
          <AlertConfirmation
            visible={showRemoveAvatarConfirmation}
            setVisible={setShowRemoveAvatarConfirmation}
            confirmAction={async () => await ProfileService.removeAvatar(user.uid)}
          >
            <Dialog.Title>Delete Profile Image?</Dialog.Title>
            <Dialog.Content>
              <Paragraph>Deleting this image will remove it from your profile.</Paragraph>
            </Dialog.Content>
          </AlertConfirmation>
        )}
      </View>
      {__DEV__ && (
        <ListItem
          key="settings-uid"
          topDivider={mode !== 'dark'}
          bottomDivider={mode !== 'dark'}
          containerStyle={{ backgroundColor: theme.white }}
        >
          <ListItem.Content style={styles.rowLeft}>
            <ListItem.Title style={{ color: theme.black }}>UID</ListItem.Title>
          </ListItem.Content>
          <ListItem.Content right={true} style={styles.rowRight}>
            <ListItem.Title style={{ color: theme[400] }}>{user?.uid}</ListItem.Title>
          </ListItem.Content>
        </ListItem>
      )}
      <ListItem
        key="settings-displayname"
        topDivider={mode !== 'dark'}
        bottomDivider={mode !== 'dark'}
        containerStyle={{ backgroundColor: theme.white }}
      >
        <ListItem.Content style={styles.rowLeft}>
          <ListItem.Title style={{ color: theme.black }}>Name</ListItem.Title>
        </ListItem.Content>
        <ListItem.Content right={true} style={styles.rowRight}>
          <ListItem.Title style={{ color: theme[400] }}>{profile?.displayName}</ListItem.Title>
        </ListItem.Content>
      </ListItem>
      <ListItem
        key="settings-information-header"
        containerStyle={[listStyle.sectionHeaderContainer, { backgroundColor: theme[200] }]}
        topDivider
        bottomDivider
      >
        <ListItem.Content>
          <ListItem.Title style={[listStyle.sectionHeaderTitle, { color: theme.black }]}>
            Information
          </ListItem.Title>
        </ListItem.Content>
      </ListItem>
      <ListItem
        key="settings-contactus"
        topDivider={mode !== 'dark'}
        bottomDivider={mode !== 'dark'}
        containerStyle={{ backgroundColor: theme.white }}
        onPress={openContactUs}
      >
        <ListItem.Content>
          <ListItem.Title style={{ color: theme.black }}>Contact Us</ListItem.Title>
        </ListItem.Content>
        <ListItem.Chevron />
      </ListItem>
      <View style={styles.logoutContainer}>
        <FormButton title="Logout" style={styles.logoutButton} onPress={() => dispatch(logout())} />
      </View>
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme[100],
    },
    avatarContainer: {
      padding: 20,
      alignItems: 'center',
    },
    placeholderContainer: {
      alignItems: 'center',
      justifyContent: 'center',
      width: 96,
      height: 96,
    },
    avatar: {
      backgroundColor: theme[300],
    },
    logoutContainer: {
      padding: 10,
      alignItems: 'center',
    },
    logoutButton: {
      borderRadius: 20,
      paddingHorizontal: 30,
    },
    rowLeft: {
      flexShrink: 0,
    },
    rowRight: {
      flexGrow: 1,
    },
  })
